import React from 'react';
import { api, fmt } from '../api';
import { Empty, ErrorBanner, Loading, Pill, statusTone, useAsync } from '../ui';
import { adminFaqSections } from './Faq';

function total(rows: any[] = []) {
  return rows.reduce((sum, row) => sum + Number(row.n ?? row.total ?? 0), 0);
}

function countStatus(rows: any[] = [], status: string) {
  return rows.find((row) => row.status === status)?.n ?? 0;
}

function Metric({ label, value, detail, tone }: { label: string; value: React.ReactNode; detail?: string; tone?: 'ok' | 'warn' | 'bad' }) {
  return (
    <div className="card" style={{ margin: 0 }}>
      <div className="muted">{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.2, margin: '5px 0' }}>
        {tone ? <Pill tone={tone}>{value}</Pill> : value}
      </div>
      {detail && <div className="muted">{detail}</div>}
    </div>
  );
}

function ProviderStatus({ label, value, detail, tone }: { label: string; value: string; detail: string; tone: 'ok' | 'warn' | 'bad' }) {
  return (
    <li className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <span><strong>{label}</strong><br /><span className="muted">{detail}</span></span>
      <Pill tone={tone}>{value}</Pill>
    </li>
  );
}

export function OpsView() {
  const overview = useAsync(() => api('GET', '/api/ops/overview'), []);
  const attention = useAsync(() => api('GET', '/api/ops/attention'), []);
  const prerequisites = useAsync(() => api('GET', '/api/ops/launch-prerequisites'), []);
  const classSchedules = useAsync(() => api('GET', '/api/ops/class-schedules'), []);
  const [workerStatus, setWorkerStatus] = React.useState<'active' | 'paused' | 'deleted' | 'all'>('active');
  const [workerHealth, setWorkerHealth] = React.useState<'healthy' | 'attention' | 'inactive' | 'all'>('all');
  const [searchInput, setSearchInput] = React.useState('');
  const [workerSearch, setWorkerSearch] = React.useState('');
  const [workerPage, setWorkerPage] = React.useState(1);
  const [copiedWorkerId, setCopiedWorkerId] = React.useState<string | null>(null);
  const [copyError, setCopyError] = React.useState<string | null>(null);
  const workers = useAsync(
    () => api('GET', `/api/ops/workers?status=${workerStatus}&health=${workerHealth}&search=${encodeURIComponent(workerSearch)}&page=${workerPage}&pageSize=25`),
    [workerStatus, workerHealth, workerSearch, workerPage],
  );

  React.useEffect(() => {
    const t = setInterval(() => { overview.reload(); attention.reload(); prerequisites.reload(); classSchedules.reload(); }, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (overview.loading && !overview.data) return <Loading what="platform operations" />;
  if (overview.error) return <ErrorBanner error={overview.error} onRetry={overview.reload} />;

  const d = overview.data;
  const attentionItems = attention.data?.items ?? [];
  const workerCounts = d.workers ?? [];
  const connections = d.connections ?? [];
  const activeWorkers = countStatus(workerCounts, 'active');
  const pausedWorkers = countStatus(workerCounts, 'paused');
  const deletedWorkers = countStatus(workerCounts, 'deleted');
  const workerTotal = total(workerCounts);
  const failedConnections = connections.filter((row: any) => row.status === 'needs_reconnection' || row.status === 'failed');
  const queueDepth = Object.values(d.jobs ?? {}).reduce((sum: number, value) => sum + Number(value), 0);
  const postureBad = attentionItems.length > 0;
  const postureWarn = !postureBad && (d.staleConnections > 0 || failedConnections.length > 0);
  const workerFleet = (
    <section className="card">
      <div className="spread">
        <div>
          <h2>Worker fleet</h2>
          <p className="muted">Searchable operational inventory. Phone numbers, assignments, messages, and school records are not exposed here.</p>
        </div>
        <Pill tone={failedConnections.length ? 'warn' : 'ok'}>{failedConnections.length ? `${failedConnections.length} connection state(s) need review` : 'No connection failures reported'}</Pill>
      </div>
      <div className="grid">
        <div><h3>Assistant lifecycle</h3><ul className="plain">{workerCounts.length ? workerCounts.map((row: any) => <li key={row.status} className="row" style={{ justifyContent: 'space-between' }}><Pill tone={statusTone(row.status === 'deleted' ? 'archived' : row.status)}>{fmt.label(row.status)}</Pill><strong>{row.n}</strong></li>) : <Empty>No workers.</Empty>}</ul></div>
        <div><h3>School connections</h3><ul className="plain">{connections.length ? connections.map((row: any, index: number) => <li key={`${row.provider}:${row.status}:${index}`} className="row" style={{ justifyContent: 'space-between' }}><span>{fmt.label(row.provider)} · <Pill tone={statusTone(row.status)}>{fmt.label(row.status)}</Pill></span><strong>{row.n}</strong></li>) : <Empty>No connections.</Empty>}</ul></div>
        <div><h3>Last 24 hours</h3><p><strong>{total(d.outboundSms24h)}</strong> outbound messages</p><p><strong>{total(d.reminders24h)}</strong> reminders processed</p><p><strong>{total(d.usage30d)}</strong> recorded usage units in 30 days</p></div>
      </div>
      <form
        className="row"
        style={{ marginTop: 16 }}
        onSubmit={(event) => { event.preventDefault(); setWorkerPage(1); setWorkerSearch(searchInput.trim()); }}
      >
        <select
          aria-label="Worker status"
          value={workerStatus}
          style={{ width: 'auto' }}
          onChange={(event) => { setWorkerStatus(event.target.value as typeof workerStatus); setWorkerPage(1); }}
        >
          <option value="active">Active workers</option>
          <option value="paused">Paused workers</option>
          <option value="deleted">Deleted workers</option>
          <option value="all">All workers</option>
        </select>
        <select
          aria-label="Worker health"
          value={workerHealth}
          style={{ width: 'auto' }}
          onChange={(event) => { setWorkerHealth(event.target.value as typeof workerHealth); setWorkerPage(1); }}
        >
          <option value="all">All health states</option>
          <option value="healthy">Healthy</option>
          <option value="attention">Needs attention</option>
          <option value="inactive">Inactive</option>
        </select>
        <input
          aria-label="Search workers"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search worker name, ID, or school"
          style={{ flex: '1 1 260px', width: 'auto' }}
        />
        <button type="submit">Search</button>
        {workerSearch && <button type="button" onClick={() => { setSearchInput(''); setWorkerSearch(''); setWorkerPage(1); }}>Clear</button>}
      </form>
      {workers.loading && !workers.data && <Empty>Loading workers…</Empty>}
      {workers.error && <p className="banner bad">{workers.error}</p>}
      {copyError && <p className="banner bad" role="alert">{copyError}</p>}
      {workers.data && (workers.data.workers.length ? (
        <>
          <table style={{ marginTop: 12 }}>
            <thead><tr><th>Worker name / ID</th><th>School</th><th>Status</th><th>Health</th><th>Created</th><th>Actions</th></tr></thead>
            <tbody>{workers.data.workers.map((worker: any) => (
              <tr key={worker.id}>
                <td><strong>{worker.name}</strong><div className="mono">{worker.id}</div></td>
                <td>{worker.school_name || (worker.school_provider ? fmt.label(worker.school_provider) : 'Not connected')}{worker.school_provider && <div className="muted"><Pill tone={statusTone(worker.school_status)}>{fmt.label(worker.school_status)}</Pill> · {worker.last_sync_at ? fmt.ago(worker.last_sync_at) : 'never synced'}</div>}</td>
                <td><Pill tone={statusTone(worker.status === 'deleted' ? 'archived' : worker.status)}>{fmt.label(worker.status)}</Pill>{worker.status_reason && <div className="muted">{worker.status_reason}</div>}</td>
                <td><Pill tone={worker.health === 'healthy' ? 'ok' : worker.health === 'attention' ? 'bad' : 'warn'}>{fmt.label(worker.health)}</Pill><div className="muted">{worker.health_detail}</div></td>
                <td className="muted">{fmt.dateTime(worker.created_at)}</td>
                <td>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(worker.id);
                        setCopiedWorkerId(worker.id);
                        setCopyError(null);
                      } catch (error) {
                        setCopyError(error instanceof Error ? `Could not copy worker ID: ${error.message}` : 'Could not copy worker ID.');
                      }
                    }}
                  >
                    {copiedWorkerId === worker.id ? 'ID copied' : 'Copy ID'}
                  </button>
                </td>
              </tr>
            ))}</tbody>
          </table>
          <div className="spread" style={{ marginTop: 12 }}>
            <span className="muted">Showing {(workers.data.page - 1) * workers.data.pageSize + 1}–{Math.min(workers.data.page * workers.data.pageSize, workers.data.total)} of {workers.data.total}</span>
            <div className="row">
              <button disabled={workers.data.page <= 1} onClick={() => setWorkerPage((page) => page - 1)}>Previous</button>
              <span>Page {workers.data.page} of {Math.max(1, Math.ceil(workers.data.total / workers.data.pageSize))}</span>
              <button disabled={workers.data.page * workers.data.pageSize >= workers.data.total} onClick={() => setWorkerPage((page) => page + 1)}>Next</button>
            </div>
          </div>
        </>
      ) : <Empty>No workers match this view.</Empty>)}
    </section>
  );
  const classScheduleHealth = (
    <section className="card">
      <div className="spread">
        <div>
          <h2>Class schedule health</h2>
          <p className="muted">Calendar connections are separate from Canvas assignments. Credentials and private feed URLs are never shown.</p>
        </div>
        <Pill tone={classSchedules.data?.schedules?.some((s: any) => s.last_sync_status === 'failed' || s.stale) ? 'warn' : 'ok'}>
          {classSchedules.data?.schedules?.length ?? 0} connected
        </Pill>
      </div>
      {classSchedules.error && <p className="banner bad">{classSchedules.error}</p>}
      {classSchedules.data?.schedules?.length ? <table>
        <thead><tr><th>Student</th><th>Source</th><th>Sync</th><th>Reminder</th><th>Action</th></tr></thead>
        <tbody>{classSchedules.data.schedules.map((schedule: any) => (
          <tr key={schedule.student_id}>
            <td><strong>{schedule.preferred_name}</strong><div className="muted">{schedule.schedule_timezone}</div></td>
            <td>{fmt.label(schedule.provider)}{schedule.external_calendar_name && <div className="muted">{schedule.external_calendar_name}</div>}</td>
            <td><Pill tone={schedule.last_sync_status === 'success' ? 'ok' : 'warn'}>{fmt.label(schedule.last_sync_status || 'never')}</Pill><div className="muted">{schedule.last_sync_at ? fmt.ago(schedule.last_sync_at) : 'never synced'}</div></td>
            <td>{schedule.reminders_enabled ? `On at ${schedule.delivery_time}` : 'Off'}{schedule.next_reminder && <div className="muted">Next {fmt.dateTime(schedule.next_reminder)}</div>}</td>
            <td><button onClick={async () => { await api('POST', `/api/ops/class-schedules/${schedule.student_id}/resync`); classSchedules.reload(); }}>Resync</button></td>
          </tr>
        ))}</tbody>
      </table> : <Empty>{classSchedules.loading ? 'Loading class schedules…' : 'No class calendars connected.'}</Empty>}
    </section>
  );

  return (
    <>
      <section className="card" style={{ background: 'linear-gradient(135deg, var(--accent-soft), var(--panel))' }}>
        <div className="spread">
          <div>
            <div className="eyebrow" style={{ color: 'var(--accent)' }}>Operations</div>
            <h1 style={{ margin: 0, fontSize: 28 }}>Student Assistant platform</h1>
            <p className="muted" style={{ marginBottom: 0 }}>
              A live, privacy-preserving view of worker health, integrations, delivery, and governance.
              Refreshed {fmt.time(d.generatedAt)}.
            </p>
          </div>
          <div>
            <Pill tone={postureBad ? 'bad' : postureWarn ? 'warn' : 'ok'}>
              {postureBad ? 'Action required' : postureWarn ? 'Operational with follow-up' : 'Operational'}
            </Pill>
          </div>
        </div>
      </section>

      <div className="grid">
        <Metric label="Active workers" value={activeWorkers} detail={`${workerTotal} total retained worker records`} tone={activeWorkers ? 'ok' : 'warn'} />
        <Metric label="Paused workers" value={pausedWorkers} detail="Can be resumed by the parent" tone={pausedWorkers ? 'warn' : 'ok'} />
        <Metric label="Deleted workers" value={deletedWorkers} detail="Archived or decommissioned records" tone={deletedWorkers ? 'warn' : 'ok'} />
        <Metric label="Attention queue" value={attentionItems.length} detail="Issues requiring an operator decision" tone={attentionItems.length ? 'bad' : 'ok'} />
        <Metric label="School sync lag" value={d.staleConnections} detail="Connections older than two hours" tone={d.staleConnections ? 'warn' : 'ok'} />
        <Metric label="Background queue" value={queueDepth} detail="Queued, leased, or retrying jobs" tone={queueDepth ? 'warn' : 'ok'} />
      </div>

      {workerFleet}
      {classScheduleHealth}

      <section className="card">
        <div className="spread">
          <div>
            <h2>Service configuration</h2>
            <p className="muted">What the text-message worker needs, what is optional, and what is currently enabled.</p>
          </div>
          <div className="row">
            <Pill tone={d.providers?.sms?.simulated ? 'warn' : 'ok'}>SMS {d.providers?.sms?.simulated ? 'simulated' : 'live'}</Pill>
            <Pill tone={d.emailEnabled ? 'warn' : 'ok'}>Email {d.emailEnabled ? 'enabled' : 'disabled'}</Pill>
          </div>
        </div>
        <ul className="plain">
          <ProviderStatus
            label="Messaging"
            value={d.providers?.sms?.simulated ? 'Simulation mode' : (d.providers?.sms?.readiness?.ready ? 'Ready' : 'Not ready')}
            detail={`Provider: ${d.providers?.sms?.name ?? 'Unknown'}. ${d.providers?.sms?.readiness?.detail ?? 'Readiness is unavailable.'} Live SMS is never enabled automatically.`}
            tone={d.providers?.sms?.simulated || !d.providers?.sms?.readiness?.ready ? 'warn' : 'ok'}
          />
          <ProviderStatus
            label="Scheduler and job queue"
            value={d.schedulerEnabled ? 'Enabled' : 'Disabled'}
            detail="Required to schedule reminders, retry transient failures, and deliver messages reliably."
            tone={d.schedulerEnabled ? 'ok' : 'bad'}
          />
          <ProviderStatus
            label="School data"
            value={connections.length ? 'Connected per student' : 'No active connections'}
            detail="Required before assistant activation. A successful first sync is enough, even if no assignments are found. OAuth is not required for the current read-only per-student token connection."
            tone={connections.length ? 'ok' : 'warn'}
          />
          <ProviderStatus
            label="Reasoning"
            value={d.providers?.ai?.configured ? (d.providers?.ai?.mode ?? 'Configured') : 'Rules only'}
            detail="Optional enrichment. Deterministic commands still work without AI; the application remains authoritative for actions and data access."
            tone={d.providers?.ai?.configured ? 'ok' : 'warn'}
          />
          <ProviderStatus
            label="Email delivery"
            value={d.providers?.email?.ready ? 'Configured' : 'Not ready'}
            detail="Required for parent account verification and password recovery, not for student interactions."
            tone={d.providers?.email?.ready ? 'ok' : 'warn'}
          />
        </ul>
      </section>

      <section className="card">
        <div className="spread">
          <div><h2>Operator attention</h2><p className="muted">Prioritized issues with impact and a suggested recovery path.</p></div>
          <Pill tone={attentionItems.length ? 'bad' : 'ok'}>{attentionItems.length ? `${attentionItems.length} open` : 'Clear'}</Pill>
        </div>
        {attention.loading && !attention.data && <Empty>Loading attention queue…</Empty>}
        {attention.error && <p className="banner bad">{attention.error}</p>}
        {attention.data && (attentionItems.length ? (
          <table><thead><tr><th>Issue</th><th>Impact</th><th>Last success</th><th>Recovery</th></tr></thead><tbody>
            {attentionItems.map((it: any) => <tr key={`${it.kind}:${it.id}`}><td><Pill tone="bad">{fmt.label(it.kind)}</Pill><div className="mono">{it.id.slice(0, 8)}</div></td><td>{it.impact}<div className="muted">{it.detail}</div></td><td className="muted">{it.lastSuccess ? fmt.ago(it.lastSuccess) : '—'}</td><td>{it.recovery}</td></tr>)}
          </tbody></table>
        ) : <div className="banner ok">No operator action is currently queued.</div>)}
      </section>

      <section className="card">
        <div className="spread">
          <div>
            <h2>Admin FAQ</h2>
            <p className="muted">Technical, operational, security, integration, and lifecycle guidance for administrators.</p>
          </div>
          <a href="https://github.com/yashmalge_microsoft/student-digital-worker/blob/main/docs/admin-command-center.md" target="_blank" rel="noreferrer">Open documentation</a>
        </div>
        {adminFaqSections.map((section) => (
          <details key={section.title} style={{ marginTop: 12 }}>
            <summary><strong>{section.title}</strong></summary>
            <p className="muted">{section.description}</p>
            {section.items.map((item) => (
              <details key={item.question} style={{ margin: '8px 0 8px 16px' }}>
                <summary>{item.question}</summary>
                <div className="faq-answer">{item.answer}</div>
              </details>
            ))}
          </details>
        ))}
      </section>

      <details className="card">
        <summary><strong>Governance and launch readiness</strong></summary>
        <p className="muted">Review these controls before enabling broader production behavior.</p>
        {prerequisites.loading && !prerequisites.data && <Empty>Loading prerequisites…</Empty>}
        {prerequisites.error && <p className="banner bad">{prerequisites.error}</p>}
        {prerequisites.data && <ul className="plain">{prerequisites.data.prerequisites.map((item: any) => <li key={item.id}><div className="spread"><strong>{item.title}</strong><Pill tone={item.status === 'complete' ? 'ok' : item.status === 'blocked' ? 'bad' : 'warn'}>{fmt.label(item.status)}</Pill></div><div className="muted">{item.description}</div>{item.blocker && <div className="muted">Blocker: {item.blocker}</div>}</li>)}</ul>}
      </details>
    </>
  );
}
